<template>
    <div class="import-prepare-step">
        <Preloader :display="loading" />
        <CompassAlert
            v-if="getTypeImport.is_enabled"
            type="neutral"
            :description="alertDescription"
            :action="{
                label: alertLabel,
                callback: () => onGoToOldImport(),
            }"
            :showClose="false"
        />
        <p class="documentation-caption typographies-paragraph-medium-semibold">
            {{
                $l10n.__(
                    'Avant de commencer votre import, merci de prendre connaissance de l’ensemble des informations disponibles sur cette page.',
                )
            }}
        </p>
        <p class="documentation-link typographies-paragraph-medium-semibold">
            {{ $l10n.__('Besoin d’aide ?') }}
            <CompassLink
                link="https://help.sellsy.com/fr/?q=import"
                target="blank"
                :label="$t.__('common.actions.view_import_data_articles')"
            />
        </p>
        <h2 class="typographies-heading-h2">
            {{ $l10n.__('Modèle de fichier') }}
        </h2>
        <p class="model-caption typographies-paragraph-medium-regular">
            {{
                $l10n.__(
                    'Pour vous assurer que la structure des données que vous allez importer dans Sellsy est conforme, téléchargez le modèle et faites correspondre vos données.',
                )
            }}
        </p>
        <div class="file-model-block">
            <div class="type-import-container">
                <CompassObject
                    v-if="isImportTitleSimple"
                    size="small"
                    :label="getTypeImport.title"
                    :object="importTypeObject"
                />
                <div
                    v-if="getTypeImport.titleSegments !== undefined"
                    class="compass-object compass-object--small"
                >
                    <CompassIcon :icon="getTypeImport.icon" size="medium" />
                    <div class="compass-object__text">
                        <h4>
                            <span
                                v-for="(segment, idx) in getTypeImport.titleSegments"
                                :key="idx"
                                :class="getImportTitleClasses(idx)"
                            >
                                {{ getImportTitleSegmentText(idx, segment) }}
                            </span>
                        </h4>
                    </div>
                </div>
            </div>
            <div class="buttons-container">
                <CompassButton
                    :label="$t.__('settings.import.ImportPrepareStep.download_xlsx_template')"
                    type="secondary"
                    :loading="isDownloadingXlsxTemplate"
                    @click="onDownloadTemplate('xlsx')"
                />
                <CompassButton
                    :label="$t.__('settings.import.ImportPrepareStep.download_csv_template')"
                    type="secondary"
                    :loading="isDownloadingCsvTemplate"
                    @click="onDownloadTemplate('csv')"
                />
            </div>
        </div>
        <!--        TABLE        -->
        <h2 class="typographies-heading-h2">{{ $l10n.__('Tableau récapitulatif des données') }}</h2>
        <p class="table-caption typographies-paragraph-medium-regular">
            {{
                $l10n.__('Ce tableau récapitule les données que vous pouvez importer dans Sellsy.')
            }}
        </p>
        <p class="mandatory-caption typographies-paragraph-medium-regular">
            {{
                $l10n.__(
                    'Les données obligatoires sont indiquées par un fond jaune et doivent être complétées afin que l’import puisse aboutir.',
                )
            }}
        </p>
        <CompassAlert
            v-if="!rateCategoriesForImport.length && hasNoDataForSetupStepMsg !== ''"
            type="error"
            :description="hasNoDataForSetupStepMsg"
            :action="{
                label: $t.__('common.actions.access_online_help'),
                callback: () => onGoToHelpPage(),
            }"
            :showClose="false"
        />
        <CompassAlert
            v-if="hasDuplicatesRateCategoriesMsg !== ''"
            type="warning"
            :description="hasDuplicatesRateCategoriesMsg"
            :showClose="false"
        />
        <Teleport to="body">
            <CompassModal
                v-model:visible="isModalVisible"
                bodyScroll
                class="import-dialog"
                :title="modalParams.name"
                @closed="closeModal"
            >
                <template #default>
                    <div v-for="values in modalParams.values" :key="useOrGenerateValue(values)">
                        <p
                            v-if="values.label"
                            class="model-caption typographies-paragraph-medium-regular"
                        >
                            <span v-if="modalParams.groupLabelSuffix">{{
                                modalParams.groupLabelSuffix
                            }}</span>
                            <span class="typographies-paragraph-medium-bold">{{
                                values.label
                            }}</span>
                        </p>
                        <p
                            v-if="modalParams.subtext"
                            class="subtext typographies-paragraph-medium-regular"
                        >
                            {{ modalParams.subtext }}
                        </p>
                        <div class="import-buttons bottom_interval">
                            <div
                                v-for="value in values.data"
                                :key="getCopyCode(value)"
                                class="import-buttons__item"
                            >
                                <CompassTooltip
                                    :content="localizedTexts.copyValue"
                                    placement="bottom"
                                >
                                    <CompassTagInteractive
                                        class="import-buttons__tag"
                                        variant="selectable"
                                        :modelValue="true"
                                        :valueLabel="value.label"
                                        @change="handleCopyToClipboardWithToast(getCopyCode(value))"
                                    />
                                </CompassTooltip>
                            </div>
                        </div>
                    </div>
                </template>
            </CompassModal>
        </Teleport>

        <div
            v-if="categoriesLength > 1"
            ref="tabCategories"
            class="tab-categories typographies-heading-h4"
        >
            <div :class="{ selected: categorySelected === 'all' }" @click="onClickTab('all')">
                <span>{{ $l10n.__('Toutes') }}</span>
                <CompassFlag size="small" :label="String(rowsFromDictionary.length)" />
            </div>
            <div
                v-for="(category, key) of categories"
                :key="key"
                :class="{ selected: categorySelected === key }"
                @click="onClickTab(key)"
            >
                <span>{{ category.title }}</span>
                <CompassFlag size="small" :label="String(getNumberByCategory(key))" />
            </div>
            <div
                v-if="Object.keys(customFieldCategories).length !== 0"
                :class="{ selected: categorySelected === 'customField' }"
                @click="onClickTab('customField')"
            >
                <span>{{ $l10n.__('Champs personnalisés') }}</span>
                <CompassFlag size="small" :label="String(numberOfCustomFields)" />
            </div>
        </div>
        <CustomTable :class="{ 'border-radius-tl-0': categoriesLength > 1 }">
            <template #header>
                <th ref="customTableThead" class="col-label typographies-paragraph-medium-bold">
                    <span>
                        {{ $l10n.__('Donnée') }}
                    </span>
                </th>
                <th class="col-description typographies-paragraph-medium-bold">
                    <span>
                        {{ $l10n.__('Description') }}
                    </span>
                </th>
                <th class="col-mandatory typographies-paragraph-medium-bold">
                    <span>
                        {{ $l10n.__('Obligatoire') }}
                    </span>
                </th>
                <th class="col-value typographies-paragraph-medium-bold">
                    <span>
                        {{ $l10n.__('Valeur') }}
                    </span>
                </th>
            </template>
            <template #content>
                <tr
                    v-for="(item, itemKey) of dataTable"
                    :key="itemKey"
                    :class="{ 'mandatory-row': item.mandatory, 'disabled-row': item.disabled }"
                >
                    <td
                        v-if="item.subtitle"
                        colspan="4"
                        class="sub-header-table-recap typographies-paragraph-medium-semibold"
                        :style="customStyleSubHeader"
                    >
                        {{ item.subtitle }}
                        <span
                            v-if="item.subheading"
                            class="typographies-paragraph-medium-semibold"
                            >{{ item.subheading }}</span
                        >
                    </td>
                    <template v-else>
                        <td class="td-data typographies-paragraph-medium-semibold">
                            <CompassTooltip
                                :content="item.dataTooltip"
                                :disabled="item.dataTooltip === null"
                                contentClass="tooltip-import-popup"
                                placement="right"
                            >
                                <span class="typographies-paragraph-medium-semibold">
                                    {{ item.label }}
                                    <CompassIcon
                                        v-if="item.dataTooltip"
                                        size="small"
                                        icon="info-circle-fill"
                                    />
                                </span>
                            </CompassTooltip>
                        </td>
                        <td class="td-description">
                            <p class="typographies-paragraph-medium-regular">
                                {{ getTextFromHtml(item.description.text) }}
                            </p>
                            <p
                                v-if="item.description.subtext"
                                class="subtext typographies-paragraph-small-regular"
                            >
                                {{ item.description.subtext }}
                            </p>
                        </td>
                        <td class="td-mandatory">
                            <CompassTooltip
                                :content="item.mandatoryTooltip"
                                :disabled="item.mandatoryTooltip === undefined"
                                :contentClass="`tooltip-import-popup ${
                                    item.mandatoryTooltipClass || ''
                                }`"
                                placement="right"
                            >
                                <p class="typographies-paragraph-medium-regular">
                                    {{ $filters.booleanFilter(item.mandatory) }}

                                    <CompassIcon
                                        v-if="item.mandatoryTooltip"
                                        type="warning"
                                        size="small"
                                        icon="warning-fill"
                                    />
                                </p>
                            </CompassTooltip>
                        </td>
                        <td class="td-value typographies-paragraph-medium-regular">
                            <template
                                v-if="['checkbox', 'radio', 'select'].includes(item.value.text)"
                            >
                                <p>
                                    {{ getValueLabel(item.value.text) }}
                                </p>
                                <p v-if="item.value.description">
                                    {{ item.value.description }}
                                </p>
                                <p class="subtext typographies-paragraph-small-regular">
                                    {{ localizedTexts.commaSeparatedValues }}
                                </p>
                                <div class="import-buttons">
                                    <div
                                        v-for="(val, index) in item.parameters.items.slice(0, 3)"
                                        :key="index"
                                        class="import-buttons__item"
                                    >
                                        <CompassTooltip
                                            :content="localizedTexts.copyValue"
                                            placement="bottom"
                                        >
                                            <CompassTagInteractive
                                                class="import-buttons__tag"
                                                variant="selectable"
                                                :modelValue="true"
                                                :valueLabel="val.label"
                                                @change="handleCopyToClipboardWithToast(val.label)"
                                            />
                                        </CompassTooltip>
                                    </div>
                                </div>
                                <CompassButton
                                    v-if="item.parameters.items.length > 3"
                                    :label="localizedTexts.showAllValues"
                                    type="tertiary"
                                    size="small"
                                    @click="showModalComplexCF(item)"
                                />
                            </template>
                            <ImportValueList
                                v-else-if="productServiceMetadata && item.valueFrom"
                                :showModal="showModal"
                                :localizedTexts="localizedTexts"
                                :item="item"
                                :values="productServiceMetadata[item.valueFrom.source]"
                            />
                            <ImportValueList
                                v-else-if="opportunityMetadata && item.valueFrom"
                                :showModal="showModalOpportunity"
                                :localizedTexts="localizedTexts"
                                :item="item"
                                :values="opportunityMetadata[item.valueFrom.source]"
                            />
                            <template v-else>
                                <p>
                                    {{ getValueLabel(item.value.text) }}
                                </p>
                                <p v-if="item.value.description">
                                    {{ item.value.description }}
                                </p>
                                <p
                                    v-if="item.value.subtext"
                                    class="subtext typographies-paragraph-small-regular"
                                >
                                    {{ item.value.subtext }}
                                </p>
                                <a
                                    v-if="item.value.link"
                                    :href="item.value.link.href"
                                    target="_blank"
                                    class="typographies-paragraph-small-semibold"
                                >
                                    {{ item.value.link.label }}
                                </a>
                            </template>
                        </td>
                    </template>
                </tr>
            </template>
        </CustomTable>
    </div>
</template>

<script>
const ParamsDictionnariesNames = {
    product: 'ImportProductParamDictionary',
    'product-variation': 'ImportProductVariationParamDictionary',
    service: 'ImportServiceParamDictionary',
    company: 'ImportCompanyDictionary',
    contact: 'ImportContactDictionary',
    'service-price': 'ImportServicePriceParamDictionary',
    'product-price': 'ImportProductPriceParamDictionary',
    opportunity: 'ImportOpportunityParamDictionary',
};

import { handleCopyToClipboardWithToast } from 'Helpers/stringHelper';

import CustomTable from 'vcomponents/utilities/CustomTable.vue';
import ImportValueList from 'vcomponents/settings/import/ImportValueList.vue';
import importService from 'api/import/importService';
import AccountingService from 'api/accountingService';
import VatService from 'api/taxes/vatService';
import { getAllUnits } from 'api/settings/unitService';
import { uniq } from 'lodash';
import OpportunitiesService from 'api/opportunities/opportunitiesService';
import Preloader from 'vcomponents/utilities/Preloader.vue';
import { createNamespacedHelpers } from 'vuex';
import importsMixins from 'vcomponents/settings/import/importsMixins.js';
import CustomFieldsDictionary from 'Dictionaries/CustomFieldsDictionary';
import Feature from 'Services/Feature';
import rateCategoriesStore from 'stores/rateCategories/rateCategoriesStore';
const importsStore = createNamespacedHelpers('importsStore');
const rateCategories = createNamespacedHelpers('rateCategories');
import OpportunitiesStatus from 'Config/authenticated/OpportunitiesStatus';

export default {
    components: {
        CustomTable,
        Preloader,
        ImportValueList,
    },
    mixins: [importsMixins],
    data() {
        return {
            categories: [],
            cf: [],
            customFieldCategories: {},
            rowsFromDictionary: [],
            categorySelected: 'all',
            loading: true,
            isDownloadingCsvTemplate: false,
            isDownloadingXlsxTemplate: false,
            importParams: null,
            customStyleSubHeader: null,
            numberOfCustomFields: null,
            rateCategoriesStore: rateCategoriesStore,
            hasDuplicatesRateCategoriesMsg: '',
            rateCategoriesForImport: [],
            duplicateCatLabels: [],
            hasNoDataForSetupStepMsg: '',
            productServiceMetadata: null,
            opportunityMetadata: null,
            isModalVisible: false,
            modalParams: {},
            localizedTexts: {
                copyValue: this.$l10n.__('Copier la valeur'),
                showAllValues: this.$l10n.__('Afficher toutes les valeurs'),
                commaSeparatedValues: this.$l10n.__('Valeurs à séparer par une virgule'),
            },
        };
    },
    computed: {
        ...importsStore.mapGetters(['getTypeImport']),
        ...rateCategories.mapGetters(['rateCategories']),
        importTypeObject() {
            return this.getTypeImport.icon.replace(/obj-slsy-/, '');
        },
        dataTable() {
            if (this.loading) return [];
            let data = [];

            if (this.categorySelected === 'all') {
                data = this.getAllImportColumns();
            } else if (this.categorySelected === 'customField') {
                for (const key of this.orderedCustomFieldCategories) {
                    if (this.customFieldCategories[key] !== undefined) {
                        data.push(this.customFieldCategories[key]);
                    }
                    const rowsFiltered = this.rowsFromDictionary.filter(
                        (item) => item.category === key,
                    );
                    data.push(...rowsFiltered);
                }
            } else {
                data = this.rowsFromDictionary.filter(
                    (item) => item.category === this.categorySelected,
                );
            }

            data.map((item) => {
                if (!item.dataTooltip || '' === item.dataTooltip) {
                    item.dataTooltip = null;
                }
            });

            return data;
        },
        orderedCustomFieldCategories() {
            return this.getTypeImport.customFieldCategoryDisplayOrder !== undefined
                ? this.getTypeImport.customFieldCategoryDisplayOrder
                : Object.keys(this.customFieldCategories);
        },
        categoriesLength() {
            return Object.keys(this.categories).length + (this.numberOfCustomFields > 0 ? 1 : 0);
        },
        alertDescription() {
            const description = {
                product: this.$l10n.__(
                    'L’ancienne plateforme d’import produits est encore disponible pour un temps limité.',
                ),
                'product-variation': this.$l10n.__(
                    'L’ancienne plateforme d’import déclinaisons produits est encore disponible pour un temps limité.',
                ),
                company: this.$l10n.__(
                    'L’ancienne plateforme d’import sociétés est encore disponible pour un temps limité.',
                ),
                contact: this.$t.__('settings.import.ImportSePrepareStep.alert_contact'),
                service: this.$l10n.__(
                    'L’ancienne plateforme d’import services est encore disponible pour un temps limité.',
                ),
                'service-price': this.$l10n.__(
                    'L’ancienne plateforme d’import services catégories tarifaires est encore disponible pour un temps limité.',
                ),
                'product-price': this.$l10n.__(
                    'L’ancienne plateforme d’import produits catégories tarifaires est encore disponible pour un temps limité.',
                ),
                opportunity: this.$t.__('settings.import.ImportSePrepareStep.alert_opportunity'),
            };
            return description[this.getTypeImport.type];
        },
        alertLabel() {
            const label = {
                product: this.$l10n.__('Accéder à l’ancienne plateforme d’import produits'),
                'product-variation': this.$l10n.__(
                    'Accéder à l’ancienne plateforme d’import déclinaisons produits',
                ),
                company: this.$l10n.__('Accéder à l’ancienne plateforme d’import sociétés'),
                service: this.$l10n.__('Accéder à l’ancienne plateforme d’import services'),
                'service-price': this.$l10n.__(
                    'Accéder à l’ancienne plateforme d’import services catégories tarifaires',
                ),
                'product-price': this.$l10n.__(
                    'Accéder à l’ancienne plateforme d’import produits catégories tarifaires',
                ),
                contact: this.$t.__('settings.import.ImportPrepareStep.go_to_old_contact_import'),
                opportunity: this.$t.__(
                    'settings.import.ImportPrepareStep.go_to_old_opportunity_import',
                ),
            };
            return label[this.getTypeImport.type];
        },
        isImportTitleSimple() {
            return (
                this.getTypeImport.title !== undefined &&
                this.getTypeImport.titleSegments === undefined
            );
        },
    },
    created() {
        try {
            this.importParams = require(
                `Dictionaries/imports/${ParamsDictionnariesNames[this.getTypeImport?.type]}`,
            ).default;
            this.categories = this.importParams.categories;
            Object.assign(this.rowsFromDictionary, this.importParams.rows);
        } catch {
            this.$pushToast({
                type: 'error',
                text: this.$l10n.__(
                    "Une erreur est survenue lors de la récupération des données de l'import, merci de réessayer",
                ),
                icon: true,
            });
        }
    },
    async mounted() {
        const customFieldsSupported =
            Feature.isEnabled('import-flatfile-custom-fields') &&
            (this.getTypeImport?.customFieldsRelated ?? []);

        const customFields = customFieldsSupported ? await this.getDistinctCustomFields() : [];

        this.updateCustomFields(customFields);

        // Actually, we want only simple value of customFields for imports
        this.updateHasCustomFields(customFields.length > 0);

        this.updateViewWithCustomFields(customFields);

        if (!this.$store.state.rateCategories) {
            this.$store.registerModule('rateCategories', rateCategoriesStore);
        }
        if (
            (Feature.isEnabled('import-flatfile-service-price') &&
                this.getTypeImport?.type === 'service-price') ||
            (Feature.isEnabled('import-flatfile-product-price') &&
                this.getTypeImport?.type === 'product-price')
        ) {
            await this.fetchRateCategories()
                .then(() => {
                    this.rateCategories.sort((a, b) =>
                        a.label > b.label ? 1 : b.label > a.label ? -1 : 0,
                    );
                    this.updateViewWithRateCategories(this.rateCategories);
                })
                .catch((err) => {
                    this.$pushToast({
                        type: 'error',
                        text: this.$l10n.__('Une erreur est survenue' + err),
                        icon: true,
                    });
                })
                .finally(() => {
                    this.updateHasDataForSetupStep(this.rateCategoriesForImport.length);
                    if (!this.rateCategoriesForImport.length) {
                        this.hasNoDataForSetupStepMsg = this.$l10n.__(
                            'Impossible de continuer cet import car aucune catégorie tarifaire ne peut pas être mise à jour.',
                        );
                    }
                    if (this.duplicateCatLabels.length) {
                        const labelList = this.duplicateCatLabels.map((item) => item).join(', ');
                        this.hasDuplicatesRateCategoriesMsg = window.i18n.__(
                            'Au moins deux catégories tarifaires ont le même nom (%(labelList)s). Elles ne pourront pas être mises à jour lors de l’import.',
                            { labelList: labelList },
                        );
                        if (!this.rateCategoriesForImport.length) {
                            this.hasDuplicatesRateCategoriesMsg = '';
                            this.hasNoDataForSetupStepMsg = window.i18n.__(
                                'Impossible de continuer cet import car aucune des catégories tarifaires ne peuvent être mises à jour. Au moins deux catégories tarifaires ont le même nom (%(labelList)s).',
                                { labelList: labelList },
                            );
                        }
                    }
                });
        }

        if (['service', 'product'].includes(this.getTypeImport?.type)) {
            Promise.all([
                this.getAccountingCodes(),
                getAllUnits({ order: 'rank', limit: 100 }),
                VatService.getAllTaxes(),
            ]).then(([accountingCodes, units, taxes]) => {
                this.productServiceMetadata = {
                    purchaseAccountingCodes: this.filteredAccountingCodes(
                        accountingCodes,
                        'purchase',
                    ),
                    sellAccountingCodes: this.filteredAccountingCodes(accountingCodes, 'sell'),
                    units: uniq(units.map(({ label }) => label)).map((label) => ({
                        label,
                    })),
                    taxes: uniq(taxes.map((vat) => vat.rate.toString())).map((label) => ({
                        label,
                    })),
                };
            });
        }

        if (this.getTypeImport?.type === 'opportunity') {
            this.opportunityMetadata = await this.getOpportunityMetadata();
        }

        this.customStyleSubHeader = `top:${this.$refs.customTableThead?.clientHeight}px`;
        this.loading = false;
    },
    methods: {
        handleCopyToClipboardWithToast,
        ...importsStore.mapActions([
            'updateCustomFields',
            'updateFields',
            'updateHasCustomFields',
            'updateHasDataForSetupStep',
        ]),
        ...rateCategories.mapActions(['fetchRateCategories']),
        onClickTab(key) {
            this.categorySelected = key;
        },
        closeModal() {
            this.isModalVisible = false;
            this.modalParams = {};
        },
        onGoToOldImport() {
            this.openLegacyImport(this.getTypeImport?.type);
        },
        filteredAccountingCodes(accountingCodes, view) {
            return accountingCodes
                .filter((val) => val.views.includes(view))
                .map((val) => ({ label: `${val.code} - ${val.label}`, copyCode: val.code }));
        },
        showModalComplexCF(item) {
            this.showModal(
                {
                    modalName: this.$l10n.__('Afficher toutes les valeurs de ') + item.label,
                    subtext: this.localizedTexts.commaSeparatedValues,
                },
                item.parameters.items.map(({ label }) => ({ label })),
            );
        },
        showModalOpportunity(valueFrom, values) {
            if (valueFrom.source === 'steps') {
                this.modalParams = {
                    name: valueFrom.modalName,
                    values: values.map(({ label, steps }) => ({ label, data: steps })),
                    groupLabelSuffix: valueFrom.modalLabelGroupSuffix,
                };
                this.isModalVisible = true;
                return;
            }

            this.showModal(valueFrom, values);
        },
        showModal(valueFrom, values) {
            this.modalParams = {
                name: valueFrom.modalName,
                subtext: valueFrom.subtext ?? null,
                values: [{ data: values }],
            };
            this.isModalVisible = true;
        },
        async getDistinctCustomFields() {
            const relatedCustomFields = this.getTypeImport?.customFieldsRelated ?? [];

            if (relatedCustomFields.length === 0) {
                return relatedCustomFields;
            }

            const searchCustomFields = async (relatedCustomField) =>
                await importService
                    .fetchImportCustomFields({
                        related_object: relatedCustomField.category,
                    })
                    .then((customFields) =>
                        customFields.map((field) => {
                            field.relatedCustomField = relatedCustomField;
                            return field;
                        }),
                    )
                    .catch(() => {
                        this.$pushToast({
                            type: 'error',
                            text: this.$l10n.__(
                                'Une erreur est survenue lors de la récupération des champs personnalisés, merci de réessayer ultérieurement',
                            ),
                            icon: true,
                        });
                    });

            const groupedCustomFields = await Promise.all(
                relatedCustomFields.map(searchCustomFields),
            );
            let result = {};

            for (const grouped of groupedCustomFields) {
                for (const customField of grouped) {
                    result[customField.code] = customField;
                }
            }

            result = Object.values(result);

            result.sort((a, b) => {
                const nameA = a.name.toUpperCase(); // ignore upper and lowercase
                const nameB = b.name.toUpperCase(); // ignore upper and lowercase

                if (nameA < nameB) {
                    return -1;
                }
                if (nameA > nameB) {
                    return 1;
                }
                // names must be equal
                return 0;
            });

            return result;
        },
        updateViewWithCustomFields(customFields) {
            for (const cf of customFields) {
                let subtext = '';
                let cfRelated = Object.assign({}, cf.relatedCustomField);
                this.numberOfCustomFields++;
                if (this.getTypeImport.type === 'company') {
                    [cfRelated, subtext] = this.populateCFForCompanyImport(cf, cfRelated, subtext);
                }
                this.customFieldCategories[cfRelated.customField] = {
                    title: cfRelated.title,
                    subtitle: cfRelated.subtitle,
                    subheading: cfRelated.subheading,
                };
                this.rowsFromDictionary.push({
                    category: cfRelated.customField,
                    label: cf.name,
                    description: {
                        text: cf.description,
                        subtext,
                    },
                    mandatory: cf.mandatory,
                    value: { text: cf.type },
                    parameters: cf.parameters,
                });
            }
        },
        populateCFForCompanyImport(cf, cfRelated, subtext) {
            if (
                ['supplier', 'client', 'prospect'].some((item) =>
                    cf.related_objects.includes(item),
                ) &&
                cf.related_objects.includes('contact')
            ) {
                cfRelated.customField = 'company_contact';
                cfRelated.subheading = this.$l10n.__(' / Sociétés et contacts');
            }

            if (!_.isEqual(cf.related_objects, ['contact'])) {
                subtext = this.$l10n.__('Utilisable sur les sociétés de type');
                const matchingTypes = [];
                const objCompanyTypes = {
                    client: this.$l10n.__('client'),
                    prospect: this.$l10n.__('prospect'),
                    supplier: this.$l10n.__('fournisseur'),
                };
                for (const [key, value] of Object.entries(objCompanyTypes)) {
                    if (cf.related_objects.includes(key)) matchingTypes.push(value);
                }
                if (matchingTypes.length > 0 && matchingTypes.length < 3) {
                    subtext += ` "${matchingTypes.join('", "')}"`;
                } else {
                    subtext = this.$l10n.__('Utilisable sur tous les types de société');
                }
            }
            return [cfRelated, subtext];
        },
        getNumberByCategory(category) {
            return this.rowsFromDictionary.reduce((count, element) => {
                return element.category === category ? count + 1 : count;
            }, 0);
        },
        onDownloadTemplate(extension) {
            const data = { columns: [], title: null };
            switch (extension) {
                case 'csv':
                    data['columns'] = this.getColumnsLabels();
                    this.isDownloadingCsvTemplate = true;
                    break;
                case 'xlsx':
                    data['columns'] = this.getLabelsForExcelTemplate();
                    data['title'] = this.getImportTemplateTitle();
                    this.isDownloadingXlsxTemplate = true;
                    break;
            }

            importService
                .fetchImportModel(data, extension)
                .then((response) => {
                    const url = window.URL.createObjectURL(new Blob([response.data]));
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = this.getImportTemplateFileName(extension);
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                })
                .catch(() => {
                    this.$pushToast({
                        type: 'error',
                        text: this.$l10n.__('Une erreur est survenue'),
                        close: true,
                        unique: true,
                        icon: true,
                    });
                })
                .finally(() => {
                    switch (extension) {
                        case 'csv':
                            this.isDownloadingCsvTemplate = false;
                            break;
                        case 'xlsx':
                            this.isDownloadingXlsxTemplate = false;
                            break;
                    }
                });
        },
        getAllImportColumns() {
            const data = [];

            for (const key in this.categories) {
                if (this.categories[key] !== undefined) {
                    data.push(this.categories[key]);
                }
                const rowsFiltered = this.rowsFromDictionary.filter(
                    (item) => item.category === key,
                );
                data.push(...rowsFiltered);
            }

            for (const key of this.orderedCustomFieldCategories) {
                if (this.customFieldCategories[key] !== undefined) {
                    data.push(this.customFieldCategories[key]);
                }
                const rowsFiltered = this.rowsFromDictionary.filter(
                    (item) => item.category === key,
                );
                data.push(...rowsFiltered);
            }

            return data;
        },
        getColumnsLabels() {
            const labels = [];
            const columns = this.getAllImportColumns();
            columns.forEach((item) => {
                if (
                    !(item.disabled !== undefined && item.disabled) &&
                    item.label !== null &&
                    item.label !== undefined
                ) {
                    labels.push({ name: item.label });
                }
            });
            return labels;
        },
        getLabelsForExcelTemplate() {
            const labels = [];
            for (const key in this.categories) {
                const rowsFiltered = this.rowsFromDictionary.filter(
                    (item) => item.category === key,
                );
                rowsFiltered.forEach((item) => {
                    if (
                        item.category !== undefined &&
                        item.label !== null &&
                        item.label !== undefined
                    ) {
                        if (item.disabled !== undefined && item.disabled) {
                            return;
                        }
                        const object = {
                            name: item.label,
                            mandatory: false,
                            custom_field: false,
                        };
                        if (item.mandatory === true) {
                            object.mandatory = true;
                        }
                        labels.push(object);
                    }
                });
            }
            for (const key of this.orderedCustomFieldCategories) {
                const rowsFiltered = this.rowsFromDictionary.filter(
                    (item) => item.category === key,
                );
                rowsFiltered.forEach((item) => {
                    if (
                        item.category !== undefined &&
                        item.label !== null &&
                        item.label !== undefined
                    ) {
                        const object = {
                            name: item.label,
                            mandatory: false,
                            custom_field: true,
                        };
                        if (item.mandatory === true) {
                            object.mandatory = true;
                        }
                        labels.push(object);
                    }
                });
            }
            return labels;
        },
        getTextFromHtml(html) {
            const parser = new DOMParser();
            return parser.parseFromString(html, 'text/html').body.textContent;
        },
        getValueLabel(value) {
            return CustomFieldsDictionary[value]?.name ?? value;
        },
        getImportTitleClasses(idx) {
            //need to remove all code related to titleSegment
            return `compass-heading compass-heading--5 ${
                idx < this.getTypeImport.titleSegments.length - 1
                    ? 'compass-heading--secondary'
                    : ''
            }`;
        },
        getImportTitleSegmentText(idx, segment) {
            return `${segment}${idx < this.getTypeImport.titleSegments.length - 1 ? ' > ' : ''}`;
        },
        getImportTemplateFileName(extension) {
            const tpl = this.$l10n.__('modèle');
            let title = this.getTypeImport?.title;
            if (['service-price', 'product-price'].includes(this.getTypeImport?.type)) {
                title = this.$l10n.__('Catégorie tarifaire');
            }
            title = title.replace(/\s/g, '_');
            return `${tpl}_${title}.${extension}`.toLowerCase();
        },
        getImportTemplateTitle() {
            const tpl = this.$l10n.__('Modèle');
            let title = this.getTypeImport?.title;
            if (this.getTypeImport.titleSegments !== undefined) {
                title = this.getTypeImport.titleSegments.map((item) => item).join(' ');
            }
            return `${tpl} ${title}`;
        },
        updateViewWithRateCategories(rateCategories) {
            this.duplicateCatLabels = this.getDuplicatedRateCategoryLabels(rateCategories);
            this.rateCategoriesForImport = [];
            const rateCategoriesFields = [];
            for (const rateCategory of rateCategories) {
                const dictionaryRow = {
                    category: this.getTypeImport?.type,
                    label: `${rateCategory.label}`,
                    description: {
                        text: window.i18n.__('Nom de la catégorie tarifaire'),
                        subtext: '',
                    },
                    mandatory: false,
                    mandatoryTooltip: window.i18n.__(
                        'Au moins une des catégories tarifaires doit être renseignée.',
                    ),
                    value: { text: window.i18n.__('Numérique (2 décimales)') },
                    disabled: false,
                    dataTooltip: null,
                };
                if (rateCategory.price.type === 'percent') {
                    dictionaryRow.disabled = true;
                    dictionaryRow.mandatoryTooltip = undefined;
                    dictionaryRow.dataTooltip = window.i18n.__(
                        'Cette catégorie tarifaire se calcule sur un pourcentage du prix de référence du produit, elle ne peut pas être mise à jour lors de l’import de catégorie tarifaire. Vous devez mettre à jour le prix de reference via un import produit.',
                    );
                }

                if (!this.duplicateCatLabels.includes(rateCategory.label)) {
                    this.rowsFromDictionary.push(dictionaryRow);
                    if (!dictionaryRow.disabled) {
                        this.rateCategoriesForImport.push({
                            name: rateCategory.label,
                        });
                        rateCategoriesFields.push({
                            type: 'number',
                            key: rateCategory.label,
                            label: rateCategory.label,
                        });
                    }
                }
            }

            this.updateFields([...(this.getTypeImport.fields ?? []), ...rateCategoriesFields]);
        },
        getDuplicatedRateCategoryLabels(rateCategories) {
            const labelArray = rateCategories.map((item) => item.label);
            const uniqueCatLabels = new Set(labelArray);

            const duplicates = [];
            labelArray.forEach(function (item) {
                if (uniqueCatLabels.has(item)) {
                    uniqueCatLabels.delete(item);
                } else if (!duplicates.includes(item)) {
                    duplicates.push(item);
                }
            });

            return duplicates;
        },
        onGoToHelpPage() {
            let helpUrl = 'https://help.sellsy.com/fr/';
            if (this.duplicateCatLabels.length) {
                helpUrl = helpUrl + '?q=import+categorie+tarifaire';
            }
            Utils.relocate(helpUrl, true);
        },
        async fetchAccountingCodes(offset = null) {
            return await AccountingService.searchAccountingCodesPerType(
                ['purchase', 'sell'],
                offset,
            );
        },
        async getAccountingCodes() {
            let offset = null;
            let codes = [];
            let pagination;
            do {
                const response = await this.fetchAccountingCodes(offset);
                pagination = response.pagination;
                codes = [...codes, ...response.data];
                offset = pagination.offset;
            } while (pagination.count === pagination.limit);

            return codes.sort((a, b) => {
                return b.label.length - a.label.length;
            });
        },
        async getOpportunityMetadata() {
            const metadata = await OpportunitiesService.getMetadata();
            const statuses = Object.values(OpportunitiesStatus).map(({ label }) => ({ label }));
            const pipelines = metadata.pipelines.map(({ label }) => ({ label }));
            const staffs = metadata.staffs.map(({ label }) => ({ label }));
            const sources = metadata.categories.flatMap((category) =>
                (category?.sources ?? []).map(({ label }) => ({ label })),
            );
            const steps = metadata.pipelines; // steps indexed by pipelines
            return {
                statuses,
                pipelines,
                staffs,
                sources,
                steps,
            };
        },
        getCopyCode(value) {
            return value?.copyCode ?? value.label;
        },
        useOrGenerateValue(value) {
            return value?.label ?? crypto.randomUUID();
        },
    },
};
</script>

<style lang="scss">
@use '~@sellsy/styleguide/src/_variables' as *;

.import-buttons {
    display: flex;
    align-items: flex-start;
    flex-wrap: wrap;

    &__item {
        margin-bottom: var(--spacing-0);
    }

    &__item:not(:last-child) {
        margin-right: var(--spacing-0);
    }

    &__tag {
        cursor: pointer;
        background: var(--colors-common-background-subtle, $gray-0);

        &:hover {
            background: var(--colors-common-background-colored-hover, rgba(17, 17, 17, 0.1));
        }
    }

    &.bottom_interval {
        margin-bottom: var(--spacing-5);
    }
    &__tertiary {
        gap: var(--spacing-0, 4px);
        border-radius: 4px;
        padding: 6px var(--spacing-1, 8px) 6px var(--spacing-1, 8px);
        font-size: var(--font-size-14, 14px);
        font-weight: var(--font-weights-proxima-nova-1, 400);
        line-height: 1.25rem;
        display: inline-block;
        cursor: pointer;
        color: $primary-50;
        transition: all 100ms cubic-bezier(0, 0, 0.3, 1);

        &:hover {
            background-color: $primary-05;
            border-radius: 4px;
        }
    }
}
.import-dialog {
    .subtext {
        margin-top: 0;
    }
    .import-buttons {
        flex-wrap: wrap;
    }

    &__title {
        margin: var(--spacing-5) var(--spacing-5) var(--spacing-2);
    }
}
.import-prepare-step {
    line-height: var(--line-heights-20);
    h1 {
        line-height: 29px;
    }

    h2 {
        line-height: var(--line-heights-24);
        margin: 0;
    }
    p {
        font-weight: var(--font-weights-proxima-nova-1);
    }
    .documentation-caption {
        font-weight: var(--font-weights-proxima-nova-0);
        margin: 0;
    }
    .documentation-link {
        color: var(--colors-common-text-standard-caption);
        font-weight: var(--font-weights-proxima-nova-0);
        margin-bottom: var(--spacing-5);
        margin-top: 0;
    }
    .mandatory-caption {
        border-left: 5px solid var(--colors-common-background-warning-default);
        color: var(--colors-common-text-standard-caption);
        margin-top: 0;
        margin-bottom: var(--spacing-3);
        padding-left: var(--spacing-0);
    }
    .mandatory {
        border-left: var(--colors-common-background-warning-default) solid var(--spacing-0);
    }
    .model-caption {
        margin-bottom: var(--spacing-3);
        margin-top: 0;
    }
    .table-caption {
        margin-bottom: var(--spacing-1);
        margin-top: 0;
    }
    .file-model-block {
        align-items: center;
        background-color: var(--colors-component-background-bloc-standard-default);
        border-radius: var(--spacing-0);
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        margin-bottom: var(--spacing-5);
        padding: var(--spacing-3);

        .buttons-container {
            align-items: stretch;
            display: flex;
            flex-direction: row;
            gap: var(--spacing-1);
        }

        .type-import-container {
            align-items: center;
            gap: var(--spacing-3);

            .compass-icon {
                height: var(--spacing-5);
                width: var(--spacing-5);
            }
        }
    }

    .duplicate-rate-categories {
        &:deep(.el-alert__content) {
            .el-alert__description {
                margin: 0;
                display: flex;
            }
        }
    }

    .custom-table {
        padding-bottom: var(--spacing-6);
        &.border-radius-tl-0 {
            &:deep(table) {
                border-top-left-radius: 0;
            }
        }

        .col-label {
            width: 23%;
        }

        .col-description {
            width: 31%;
        }

        .col-mandatory {
            width: 14%;
        }

        .col-value {
            width: 32%;
        }

        thead {
            th:first-child {
                border-top-left-radius: var(--spacing-0);
            }

            th:last-child {
                border-top-right-radius: var(--spacing-0);
            }
        }

        th {
            background-color: var(--colors-common-background-default);
            position: sticky;
            top: 0;
            z-index: 1001;
        }

        td {
            padding: var(--spacing-0) var(--spacing-3);
        }

        .mandatory-row {
            background-color: var(--colors-common-background-warning-subtle);
            td:first-child {
                border-left: var(--colors-common-background-warning-default) solid 5px;
            }
        }

        .disabled-row {
            td {
                color: #8c909c;
            }
        }

        .sub-header-table-recap {
            background-color: var(--colors-common-background-subtle);
            padding: 10px var(--spacing-3);
            position: sticky;
            z-index: 1001;
            span {
                color: var(--colors-common-text-standard-caption);
            }
        }
        .td-description,
        .td-value {
            padding: var(--spacing-1) var(--spacing-3);
            .subtext {
                color: var(--colors-common-text-standard-caption);
            }
            a {
                margin: 0;
            }
            p {
                margin: 0;
            }
        }
        .td-mandatory,
        .td-data {
            p {
                display: flex;
                flex-direction: row;
                gap: var(--spacing-0);
            }
            CompassIcon {
                margin-left: var(--spacing-0);
            }
        }
    }

    .tab-categories {
        display: flex;
        flex-direction: row;
        > div {
            align-items: center;
            border: 1px solid var(--colors-common-border-default);
            border-bottom: 0 !important;
            cursor: pointer;
            display: flex;
            flex-direction: row;
            gap: var(--spacing-0);
            padding: 6px var(--spacing-4);
            transition: background-color 100ms cubic-bezier(0, 0, 0.3, 1);

            &:hover {
                background-color: var(--colors-common-background-primary-subtle);
            }
        }
        .compass-flag {
            background-color: var(--colors-common-background-primary-hover-alt);
            color: var(--colors-common-background-primary-default);
            padding: 0 6px;
        }

        .selected {
            background-color: var(--colors-common-background-primary-hover);
            color: var(--colors-common-text-standard-on-color);

            &:hover {
                background-color: var(--colors-common-background-primary-hover);
                color: var(--colors-common-text-standard-on-color);
            }

            .compass-flag {
                background: var(--colors-common-background-contrast-hover);
                color: var(--colors-common-text-standard-on-color);
            }
        }

        > div:first-child {
            border-top-left-radius: var(--spacing-0);
        }

        > div:not(:last-child) {
            border-right: none;
        }

        > div:last-child {
            border-top-right-radius: var(--spacing-0);
        }
    }

    .compass-object__text h4 {
        margin: 0;
    }
}
</style>

<style lang="css">
.tooltip-import-popup.w-264 {
    width: 264px;
}
</style>
